/**
 * Быстрое добавление смены в график (manager design v3 §8.1 «+ Смена»).
 *
 * Поток: выбрать отдел → график этого отдела → сотрудника → шаблон смены
 * (префилл времени/отдела) → дату → POST /v1/scheduling/shifts. Шаблон —
 * необязателен; без него время вводится вручную.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card } from '@hsc/ui';
import { createShift, listSchedules, listStaffMembers } from '@hsc/api-client';
import type { Schedule, ShiftCreateInput, StaffMember } from '@hsc/types';
import { useStaffDepartments } from '../staff/useStaff.js';
import { useShiftTemplates } from './useShiftTemplates.js';

function todayISODate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/** Собрать RFC3339 из даты (YYYY-MM-DD) и времени (HH:MM). */
function toRFC3339(date: string, hhmm: string): string {
  return new Date(`${date}T${hhmm}:00`).toISOString();
}

export function AddShiftForm({
  onClose,
  departmentId,
}: {
  onClose: () => void;
  departmentId?: string | undefined;
}): JSX.Element {
  const qc = useQueryClient();
  const deptsQuery = useStaffDepartments();
  const departments = deptsQuery.data ?? [];

  const [dept, setDept] = useState<string>(departmentId ?? '');
  const [scheduleId, setScheduleId] = useState<string>('');
  const [staffId, setStaffId] = useState<string>('');
  const [templateId, setTemplateId] = useState<string>('');
  const [date, setDate] = useState<string>(todayISODate());
  const [start, setStart] = useState<string>('08:00');
  const [end, setEnd] = useState<string>('16:00');
  const [err, setErr] = useState<string | null>(null);

  const templatesQuery = useShiftTemplates();
  const templates = templatesQuery.data ?? [];

  const schedulesQuery = useQuery<Schedule[]>({
    queryKey: ['scheduling', 'schedules', dept],
    queryFn: async () => (await listSchedules(dept)).items,
    enabled: dept !== '',
    staleTime: 30_000,
  });
  const schedules = schedulesQuery.data ?? [];

  const membersQuery = useQuery<StaffMember[]>({
    queryKey: ['staff', 'members', dept],
    queryFn: async () => (await listStaffMembers(dept)).items,
    enabled: dept !== '',
    staleTime: 60_000,
  });
  const members = membersQuery.data ?? [];

  // Шаблоны, подходящие отделу (или общие).
  const deptTemplates = useMemo(
    () => templates.filter((t) => !t.department_id || t.department_id === dept),
    [templates, dept],
  );

  const applyTemplate = (id: string): void => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) {
      setStart(t.start_time);
      setEnd(t.end_time);
      if (t.department_id && t.department_id !== dept) setDept(t.department_id);
    }
  };

  const mutation = useMutation({
    mutationFn: (input: ShiftCreateInput) => createShift(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scheduling'] });
      onClose();
    },
    onError: (e: Error) => setErr(e.message || 'Не удалось создать смену'),
  });

  const canSubmit = scheduleId !== '' && staffId !== '' && date !== '' && start !== '' && end !== '';

  const submit = (): void => {
    setErr(null);
    // Конец смены раньше начала (через полночь) — переносим на следующий день.
    const startISO = toRFC3339(date, start);
    let endISO = toRFC3339(date, end);
    if (new Date(endISO) <= new Date(startISO)) {
      const nextDay = new Date(`${date}T00:00:00`);
      nextDay.setDate(nextDay.getDate() + 1);
      const nd = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(
        nextDay.getDate(),
      ).padStart(2, '0')}`;
      endISO = toRFC3339(nd, end);
    }
    mutation.mutate({
      schedule_id: scheduleId,
      staff_member_id: staffId,
      scheduled_start: startISO,
      scheduled_end: endISO,
      allow_past: true,
    });
  };

  return (
    <Card>
      <form
        className="hsc-stack sched-addform"
        data-testid="shift-add-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label className="sched-field">
          <span>Отдел</span>
          <select
            value={dept}
            onChange={(e) => {
              setDept(e.target.value);
              setScheduleId('');
              setStaffId('');
            }}
            data-testid="shift-dept"
          >
            <option value="">— выберите отдел —</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>

        <label className="sched-field">
          <span>Шаблон смены (префилл времени)</span>
          <select
            value={templateId}
            onChange={(e) => applyTemplate(e.target.value)}
            data-testid="shift-template"
            disabled={deptTemplates.length === 0}
          >
            <option value="">— без шаблона —</option>
            {deptTemplates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.start_time}–{t.end_time})
              </option>
            ))}
          </select>
        </label>

        <label className="sched-field">
          <span>График</span>
          <select
            value={scheduleId}
            onChange={(e) => setScheduleId(e.target.value)}
            data-testid="shift-schedule"
            disabled={dept === ''}
          >
            <option value="">— выберите график —</option>
            {schedules.map((s) => (
              <option key={s.id} value={s.id}>
                {s.period_start} — {s.period_end} (v{s.version})
              </option>
            ))}
          </select>
        </label>

        <label className="sched-field">
          <span>Сотрудник</span>
          <select
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
            data-testid="shift-staff"
            disabled={dept === ''}
          >
            <option value="">— выберите сотрудника —</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.display_name?.trim()
                  ? `${m.display_name} · ${m.position_title}`
                  : m.position_title}
              </option>
            ))}
          </select>
        </label>

        <div className="sched-field-row">
          <label className="sched-field">
            <span>Дата</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="shift-date"
            />
          </label>
          <label className="sched-field">
            <span>Начало</span>
            <input
              type="time"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              data-testid="shift-start"
            />
          </label>
          <label className="sched-field">
            <span>Конец</span>
            <input
              type="time"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              data-testid="shift-end"
            />
          </label>
        </div>

        {(err || schedules.length === 0) && dept !== '' && (
          <p className="fleet-card__err" role="alert" data-testid="shift-add-error">
            {err ??
              'У отдела нет графиков — создайте график периода, чтобы добавить в него смену.'}
          </p>
        )}

        <div className="sched-addform__actions">
          <Button type="submit" disabled={!canSubmit || mutation.isPending} data-testid="shift-submit">
            {mutation.isPending ? 'Сохранение…' : 'Создать смену'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} data-testid="shift-cancel">
            Назад
          </Button>
        </div>
      </form>
    </Card>
  );
}
